import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COUNT_MODE_OPTIONS } from '../../constants';
import type { CountModeValue } from '../../constants';

type Props = {
  value: CountModeValue;
  onChange: (v: CountModeValue) => void;
};

/** 各カウントモードボタンのホバー時に表示する説明（2行） */
const COUNT_MODE_TOOLTIP: Record<CountModeValue, [string, string]> = {
  total: ['期間内の総数を', 'カウントします。'],
  registered_cohort: ['期間内に登録した人のみ', 'をカウントします。'],
};

const VIEWPORT_PADDING = 8;
const ARROW_HEIGHT = 8;
const ARROW_HALF_WIDTH = 6;
/** 右寄せ矢印モード時の、箱の右端から矢印中心までの距離（Figma: px-[24px] + 矢印半幅6px）*/
const ARROW_RIGHT_OFFSET = 30;

/**
 * カウントモードのトグルボタン
 * 各ボタンにホバーすると説明ツールチップを表示する。
 * ツールチップは親要素の overflow に切り取られないよう React Portal で body に描画する。
 * Figma 準拠で 'total' は矢印を箱中央、'registered_cohort' は箱右端から30pxの位置に配置する。
 */
export default function CountModeToggle({ value, onChange }: Props) {
  const btnRefs = useRef(new Map<CountModeValue, HTMLButtonElement | null>());
  const boxRef = useRef<HTMLSpanElement | null>(null);
  const [hovered, setHovered] = useState<{
    mode: CountModeValue;
    top: number;
    anchorCenter: number;
  } | null>(null);
  const [boxLeft, setBoxLeft] = useState<number | null>(null);

  const showTooltip = (mode: CountModeValue) => {
    const rect = btnRefs.current.get(mode)?.getBoundingClientRect();
    if (!rect) return;
    setBoxLeft(null);
    setHovered({
      mode,
      top: rect.bottom,
      anchorCenter: rect.left + rect.width / 2,
    });
  };

  const hideTooltip = () => {
    setHovered(null);
    setBoxLeft(null);
  };

  useLayoutEffect(() => {
    if (!hovered || !boxRef.current) return;
    const w = boxRef.current.offsetWidth;
    const arrowOffsetInBox = hovered.mode === 'registered_cohort' ? w - ARROW_RIGHT_OFFSET : w / 2;
    const desired = hovered.anchorCenter - arrowOffsetInBox;
    const maxLeft = window.innerWidth - w - VIEWPORT_PADDING;
    setBoxLeft(Math.max(VIEWPORT_PADDING, Math.min(desired, maxLeft)));
  }, [hovered]);

  return (
    <div className="flex h-[42px]">
      {COUNT_MODE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          ref={(el) => {
            btnRefs.current.set(opt.value, el);
          }}
          type="button"
          onClick={() => onChange(opt.value)}
          onMouseEnter={() => showTooltip(opt.value)}
          onMouseLeave={hideTooltip}
          onFocus={() => showTooltip(opt.value)}
          onBlur={hideTooltip}
          className={`border border-solid border-border px-2 text-xs transition-colors first:rounded-l-sm last:-ml-px last:rounded-r-sm ${
            value === opt.value
              ? 'bg-primary_lighten font-bold text-primary'
              : 'border-border bg-white text-sub hover:bg-gray-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
      {hovered &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-50 flex flex-col items-start"
            style={{
              top: hovered.top,
              left: boxLeft ?? 0,
              visibility: boxLeft != null ? 'visible' : 'hidden',
            }}
          >
            <div
              aria-hidden
              className={`flex w-full ${
                hovered.mode === 'registered_cohort' ? 'justify-end pr-6' : 'justify-center'
              }`}
            >
              <span
                className="block"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: `${ARROW_HALF_WIDTH}px solid transparent`,
                  borderRight: `${ARROW_HALF_WIDTH}px solid transparent`,
                  borderBottom: `${ARROW_HEIGHT}px solid #000`,
                }}
              />
            </div>
            <span
              ref={boxRef}
              className="block rounded-sm bg-black p-2 text-xs leading-[1.7] font-normal whitespace-nowrap text-white shadow-[0_0_12px_0_rgba(0,0,0,0.2)]"
            >
              {COUNT_MODE_TOOLTIP[hovered.mode][0]}
              <br />
              {COUNT_MODE_TOOLTIP[hovered.mode][1]}
            </span>
          </div>,
          document.body
        )}
    </div>
  );
}
